
import {
  computed, ComputedRef, defineComponent, PropType, Ref, ref, onMounted,
} from 'vue';

import { useI18n } from 'vue-i18n';
import {
  FieldConfig,
  LinkTypeEnum,
  ParserFieldObject,
  Property,
} from '@/types';
import BaseLink from '@/components/fields/BaseLink.vue';
import { TemplateEditorState } from '@/composables/template-editor/TemplateEditor';
import { getVariablesWithCollectionContext, getMetadataVariables } from '@/composables/template-builder/collectionContext';
import { getCustomContactProperties } from '@/configs/custom-contact-properties';

export default defineComponent({
  name: 'Link',

  components: {
    BaseLink,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: {
    'on-change-properties': Object,
  },

  setup(props, context) {
    const { t } = useI18n();

    const mainProperties: Ref<Array<Property>> = ref(props.parserValues.properties);
    const hrefProperty: Ref<Property> = ref(mainProperties.value[0]);
    const enrichedVariables = ref(props.configs.options.variablesList || []);
    const originCall: ComputedRef<string> = computed(() => {
      let returnValue = 'templateBuilder';

      if (TemplateEditorState.template?.createdFromCampaign) {
        if ((new RegExp('_automation')).test(TemplateEditorState.template?.createdFromCampaign)) {
          returnValue = 'automation';
        } else {
          returnValue = 'newsletter';
        }
      }
      return returnValue;
    });
    const idCampaign = computed(() => TemplateEditorState.template?.idCampaign ?? null);

    const handleChange = () => {
      context.emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: [hrefProperty.value],
      });
    };

    const handleLinkTypeChange = (linkType: LinkTypeEnum) => {
      if (mainProperties.value && mainProperties.value.length > 1 && mainProperties.value[1].name === 'data-send-form') {
        mainProperties.value[1].value = linkType === LinkTypeEnum.SEND_FORM;
        context.emit('on-change-properties', {
          selector: props.parserValues.selector,
          properties: [mainProperties.value[1]],
        });
      }
    };

    const handleRemoveDataSendForm = () => {
      context.emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: mainProperties.value,
        removeAttribute: 'data-send-form',
      });
    };

    onMounted(async () => {
      let baseVariables = props.configs.options.variablesList || [];

      // Add custom contact properties to base variables (for use outside loops)
      try {
        const customSchemas = await getCustomContactProperties();
        const customVariables = customSchemas.flatMap((schema) => schema.properties.map((prop) => ({
          value: `{var=${prop.variableName}}`,
          label: `${schema.schemaName} - ${prop.name}`,
          labelWithBadge: `${schema.schemaName} - ${prop.name} <span class="custom-badge-inline">CUSTOM</span>`,
          type: prop.type,
          isCustom: true,
        })));
        baseVariables = [...baseVariables, ...customVariables];
      } catch (error) {
        console.error('Error loading custom contact properties:', error);
      }

      // Add metadata variables from custom events
      try {
        const metadataVars = await getMetadataVariables();
        baseVariables = [...baseVariables, ...metadataVars];
      } catch (error) {
        console.error('Error loading metadata variables:', error);
      }

      // Enrich variables with collection context
      if (props.parserValues.selector) {
        enrichedVariables.value = await getVariablesWithCollectionContext(
          props.parserValues.selector,
          baseVariables,
        ) as never[];
      } else {
        enrichedVariables.value = baseVariables as never[];
      }
    });

    return {
      t,
      hrefProperty,
      enrichedVariables,
      originCall,
      idCampaign,
      handleChange,
      handleLinkTypeChange,
      handleRemoveDataSendForm,
    };
  },
});
